import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Logger, LoggingService } from 'ionic-logging-service';
import { Observable, of, throwError, TimeoutError } from 'rxjs';
import { catchError, map, switchMap, take, tap, timeout } from 'rxjs/operators';
import { Broadcaster } from '../events/broadcaster.class';
import { LogsService } from '../logs/logs.service';
import { NetworkService } from '../network/network.service';
import { AuthService } from '../auth/auth.service';
import { LogoutService } from '../logout/logout.service';
import { AuthSignalService } from '../auth/auth-signal.service';
import { EnvironmentService } from '../environment/environment.service';

@Injectable({
  providedIn: 'root'
})
export class ErrorService {

  public isOnline = this.networkService.hasInternetAccess;
  public unauthenticatedSignal = signal(false);
  public stateError: Boolean = false;
  private logger: Logger;
  private checkServerHealthUrl: string;

  constructor(
    private authService: AuthService,
    private authSignalService: AuthSignalService,
    private broadcaster: Broadcaster,
    private loggingService: LoggingService,
    private logService: LogsService,
    private logoutService: LogoutService,
    private networkService: NetworkService,
    private httpClient: HttpClient,
    private environmentService: EnvironmentService,
  ) {
    this.logger = this.loggingService.getLogger("[ErrorService]");
    const methodName = "ctor";
    this.logger.entry(methodName);
  }

  public handleHttpClientResponseError(httpErrorResponse: HttpErrorResponse | any, requestMethod: string, calledFunction: string): Observable<string> {
    // Handle case where error is not actually an HttpErrorResponse (e.g., Error objects from AuthService)
    if (httpErrorResponse && !httpErrorResponse.status && httpErrorResponse.message) {
      // This is likely a regular Error object (like "No refresh token")
      this.logger.info(`Handling non-HTTP error from ${calledFunction}: ${httpErrorResponse.message}`);

      // For authentication-related errors, trigger sign-out
      if (httpErrorResponse.message.toLowerCase().includes('refresh token') ||
          httpErrorResponse.message.toLowerCase().includes('no refresh token')) {
        this.logger.info('Authentication token error - triggering sign-out');
        return this.handleAuthenticationError();
      }

      // For other errors, show modal
      this.showErrorModal(httpErrorResponse, requestMethod);
      return of('modal');
    }

    this.logService.logErrorData(httpErrorResponse, requestMethod, calledFunction);

    if (this.isOnline()) {
      const errorStatus = httpErrorResponse.status ?? null;

      // Check for authentication errors first
      if (errorStatus === 401 || errorStatus === 403) {
        // Sign out user and trigger authenticated signal
        this.authService.deAuthenticateUser().pipe(
          take(1),
          switchMap(() => this.logoutService.signOutUser().pipe(
            take(1),
            map(() => {
              // Trigger the authenticated signal
              this.authSignalService.triggerUnauthenticated();
              return 'unauthenticated';
            }),
            catchError(() => {
              this.authSignalService.triggerUnauthenticated();
              return of('unauthenticated');
            })
          )),
          catchError(() => {
            this.authSignalService.triggerUnauthenticated();
            return of('unauthenticated');
          })
        ).subscribe();

        return of('unauthenticated');
      } else {
        // Handle timeout and all other errors
        this.showErrorModal(httpErrorResponse, requestMethod);
        return of('modal');
      }
    }
    return of('modal');
  }

  public showErrorModal(httpErrorResponse: HttpErrorResponse | any, requestMethod: string) {
    this.broadcaster.broadcast('showErrorModal', ({
      httpErrorResponse: httpErrorResponse instanceof TimeoutError ? httpErrorResponse['name'] : httpErrorResponse,
      requestMethod
    }));
  }

  private handleAuthenticationError(): Observable<string> {
    this.logger.info('Handling authentication error - signing out user');
    return this.authService.deAuthenticateUser().pipe(
      take(1),
      switchMap(() => this.logoutService.signOutUser().pipe(
        take(1),
        map(() => {
          // Trigger the authenticated signal
          this.authSignalService.triggerUnauthenticated();
          return 'unauthenticated';
        }),
        catchError(() => {
          this.authSignalService.triggerUnauthenticated();
          return of('unauthenticated');
        })
      )),
      catchError(() => {
        this.authSignalService.triggerUnauthenticated();
        return of('unauthenticated');
      })
    );
  }


  // ######################################################################## //
  // ###  CHECK SERVER STATE - METHODS    ################################### //
  // ######################################################################## //

  public canConnectToServer(): Observable<boolean> {
    this.logger.info('canConnectToServer()');
    return this.checkServerHealth().pipe(
      map(() => {
        this.logger.info('Server health check successful');
        return true;
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.warn('Server health check failed:', error.status || 'unknown');
        return of(false);
      })
    );
  }

  public checkServerHealth(): Observable<any> {
    this.logger.info('checkServerHealth()');
    this.checkServerHealthUrl = this.environmentService.getEnvironmentEndpoint['checkServerHealth'];

    if (!this.checkServerHealthUrl) {
      this.logger.error('checkServerHealth: No health check URL configured');
      return throwError(() => new Error('No health check URL configured'));
    }

    return this.httpClient.get<any>(this.checkServerHealthUrl).pipe(
      timeout(10000), // 10 second timeout
      tap(() => {
        this.logger.info('Server health check response received');
      }),
      catchError((error: HttpErrorResponse) => {
        this.logger.error('Server health check failed:', {
          status: error.status,
          statusText: error.statusText,
          url: this.checkServerHealthUrl
        });
        return throwError(() => error);
      })
    );
  }

  // ######################################################################## //
  // ###  OLD DEBUG QUERY METHODS    ######################################## //
  // ######################################################################## //

  // public delayServerResponse(internalTestWaitTimeUrl: string, timeToWait: number): Observable<any> { // GET a url from PrintJobsPage, a service cannot get a variable from another service
  //   this.logger.info('delayServerResponse()');
  //   return new Observable((observer) => {
  //     this.dialogService.showLoadingSpinnerDialog('delayServerResponse()').subscribe(() => {
  //       let waitSetTimeUrl: string = internalTestWaitTimeUrl + timeToWait;
  //       this.httpClient.post<any>(waitSetTimeUrl, {})
  //       .subscribe((response: any) => {
  //         this.dialogService.hideLoadingSpinnerDialog('delayServerResponse()');
  //         observer.next(response);
  //         observer.complete();
  //       }, (httpErrorResponse: HttpErrorResponse) => {
  //         this.dialogService.hideLoadingSpinnerDialog('ERROR delayServerResponse()');
  //         this.handleHttpClientResponseError(httpErrorResponse, 'POST', 'delayedServerResponse');
  //         observer.error(httpErrorResponse);
  //         observer.complete();
  //       });
  //     });
  //   });
  // }

  // public returnSetStatusCode(internalTestReturnSetStatusCode: string, statusCode: number) {
  //   this.logger.info('returnSetStatusCode(' + statusCode + ')');
  //   return new Observable((observer) => {
  //     if (this.timer > 4) {
  //       statusCode = 200;
  //     }
  //     let returnSetStatusCodeUrl: string = internalTestReturnSetStatusCode + statusCode;
  //     this.httpClient.get<any>(returnSetStatusCodeUrl)
  //     .pipe(retryWhen(error => error.pipe(
  //       delay(1000),
  //       take(3),
  //       // return httpErrorResponse.status > 499 ? Observable.of(true) : Observable.throw(httpErrorResponse);
  //       tap((httpErrorResponse: HttpErrorResponse) => {this.httpErrorResponse = httpErrorResponse}),
  //       finalize(() => {
  //         this.handleHttpClientResponseError(this.httpErrorResponse, 'GET', '[ErrorService] returnSetStatusCode()');
  //         observer.error(this.httpErrorResponse);
  //         observer.complete();
  //       })
  //     )))
  //     .subscribe((response: any) => {
  //       this.timer = 0;
  //       observer.next(response);
  //       observer.complete();
  //     });
  //   });
  // }
}
